import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown, ArrowRight } from "lucide-react";
import { BRAND, NAV, PRODUCT_CATEGORIES, INDUSTRIES } from "@/lib/data";
import { motion, AnimatePresence } from "framer-motion";
import ThemeToggle from "./ThemeToggle";

// Keep routes/data intact; only hide selected entries from navbar rendering.
const HIDDEN_NAV_PATHS = new Set(["/products", "/custom-packaging"]);

// Which NAV entries expand into a dropdown, and how to build their items
const DROPDOWNS = {
  "/products": () =>
    PRODUCT_CATEGORIES.map((c) => ({
      label: c.name,
      to: `/products/${c.slug}`,
      testId: `nav-dropdown-product-${c.slug}`,
    })),
  "/industries": () =>
    INDUSTRIES.map((i) => ({
      label: i.name,
      to: `/industries/${i.slug}`,
      testId: `nav-dropdown-industry-${i.slug}`,
    })),
};

function DesktopNavItem({ item }) {
  const dropdownBuilder = DROPDOWNS[item.path];
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const { pathname } = useLocation();

  // Close the dropdown whenever the route changes (i.e. user selected an item)
  useEffect(() => { setOpen(false); }, [pathname]);

  if (!dropdownBuilder) {
    return (
      <NavLink
        to={item.path}
        data-testid={`nav-link-${item.label.toLowerCase()}`}
        className={({ isActive }) =>
          `px-4 py-2 text-sm tracking-wide rounded-full border transition-all ${
            isActive
              ? "text-brand bg-brand/10 border-brand/20"
              : "text-white/70 hover:text-white hover:bg-white/5 hover:border-white/10 border-transparent"
          }`
        }
      >
        {item.label}
      </NavLink>
    );
  }

  const items = dropdownBuilder();
  const handleEnter = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timerRef.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      data-testid={`nav-dropdown-wrap-${item.label.toLowerCase()}`}
    >
      <NavLink
        to={item.path}
        data-testid={`nav-link-${item.label.toLowerCase()}`}
        className={({ isActive }) =>
          `px-4 py-2 text-sm tracking-wide rounded-full border transition-all inline-flex items-center gap-1 ${
            isActive
              ? "text-brand bg-brand/10 border-brand/20"
              : "text-white/70 hover:text-white hover:bg-white/5 hover:border-white/10 border-transparent"
          }`
        }
      >
        {item.label}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </NavLink>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            className="absolute top-full left-1/2 -translate-x-1/2 pt-3"
            data-testid={`nav-dropdown-${item.label.toLowerCase()}`}
          >
            <div className="min-w-[260px] rounded-2xl bg-ink-900/95 backdrop-blur-xl border border-white/10 shadow-[0_16px_48px_rgba(0,0,0,0.5)] p-2 max-h-[70vh] overflow-y-auto">
              {items.map((d) => (
                <Link
                  key={d.to}
                  to={d.to}
                  data-testid={d.testId}
                  onClick={() => setOpen(false)}
                  className="block px-4 py-2.5 text-sm text-white/75 rounded-xl hover:bg-white/5 hover:text-brand transition"
                >
                  {d.label}
                </Link>
              ))}

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MobileNavItem({ item, onNavigate }) {
  const dropdownBuilder = DROPDOWNS[item.path];
  const [open, setOpen] = useState(false);

  if (!dropdownBuilder) {
    return (
      <Link
        to={item.path}
        onClick={onNavigate}
        className="py-3 text-base text-white/80 border-b border-white/5"
        data-testid={`mobile-nav-${item.label.toLowerCase()}`}
      >
        {item.label}
      </Link>
    );
  }

  const items = dropdownBuilder();

  return (
    <div className="border-b border-white/5">
      <div className="flex items-center justify-between py-3">
        <Link
          to={item.path}
          onClick={onNavigate}
          className="text-base text-white/80"
          data-testid={`mobile-nav-${item.label.toLowerCase()}`}
        >
          {item.label}
        </Link>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="p-2 text-white/60"
          aria-label={`toggle ${item.label}`}
          data-testid={`mobile-nav-toggle-${item.label.toLowerCase()}`}
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pb-3 pl-3 flex flex-col">
              {items.map((d) => (
                <Link
                  key={d.to}
                  to={d.to}
                  onClick={onNavigate}
                  data-testid={`mobile-${d.testId}`}
                  className="py-2 text-sm text-white/60 hover:text-brand transition"
                >
                  {d.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const visibleNavItems = NAV.filter((n) => !HIDDEN_NAV_PATHS.has(n.path));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "bg-ink-900/80 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
        }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src={BRAND.logo} alt="Pacfully" className="h-10 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1 bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-full p-1.5 shadow-[0_8px_32px_rgba(0,0,0,0.37)]">
          {visibleNavItems.map((n) => (
            <DesktopNavItem key={n.path} item={n} />
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <ThemeToggle />
          <Link
            to="/industries"
            data-testid="navbar-explore-products-button"
            className="group inline-flex items-center gap-2 bg-brand text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-brand-600 transition shadow-[0_8px_24px_rgba(244,90,42,0.35)]"
          >
            Explore Products <ArrowRight className="w-4 h-4 transition group-hover:translate-x-1" />
          </Link>
        </div>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-white"
          aria-label="menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden bg-ink-900/95 backdrop-blur-xl border-t border-white/5"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
              {visibleNavItems.map((n) => (
                <MobileNavItem key={n.path} item={n} onNavigate={() => setOpen(false)} />
              ))}
              <Link
                to="/industries"
                className="mt-4 inline-flex items-center justify-center gap-2 bg-brand text-white rounded-full px-5 py-3 text-sm font-medium"
                data-testid="mobile-explore-products-button"
                onClick={() => setOpen(false)}
              >
                Explore Products <ArrowRight className="w-4 h-4" />
              </Link>
              <div className="mt-3 flex justify-center">
                <ThemeToggle />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
